import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Spin, Alert, Button, Tag, message, Space, Popconfirm, Empty, Tooltip, Tabs,
} from "antd";
import {
  ArrowLeftOutlined, EditOutlined, ReloadOutlined, PlusOutlined, DeleteOutlined,
  DragOutlined, EyeOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import styled from "styled-components";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";
import EventPickerModal from "./EventPickerModal";

const PageWrapper = styled.div`padding: 28px 32px; max-width: 1280px;`;
const Header = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 24px; flex-wrap: wrap;
`;
const TitleBlock = styled.div``;
const Title = styled.h1`font-size: 26px; font-weight: 600; color: #1e293b; margin: 0; letter-spacing: -0.5px;`;
const Subtitle = styled.div`color: #64748b; font-size: 13px; margin-top: 4px;`;
const Banner = styled.div`
  height: 200px; border-radius: 16px; margin-bottom: 24px;
  background: ${(p) => (p.$src ? `url(${p.$src}) center/cover no-repeat` : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)")};
  position: relative; overflow: hidden;
`;
const BannerOverlay = styled.div`
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%);
  display: flex; align-items: flex-end; padding: 24px;
  color: white;
`;
const BannerName = styled.div`font-size: 28px; font-weight: 700; letter-spacing: -0.4px;`;
const Card = styled.div`
  background: white; border: 1px solid #e2e8f0; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04); padding: 24px; margin-bottom: 20px;
`;
const SectionTitle = styled.h2`font-size: 16px; font-weight: 600; color: #1e293b; margin: 0 0 16px;`;
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatBox = styled.div`
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 96px;
`;
const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.6px;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;
const StatValueMuted = styled.span`
  color: #94a3b8;
  font-weight: 500;
  font-size: 16px;
  margin-left: 5px;
`;
const StatLabel = styled.div`
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 600;
  margin-top: 8px;
`;
const StatHint = styled.div`
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
  font-weight: 500;
`;
const EventRow = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 12px;
  border: 1px solid ${(p) => (p.$dragging ? "#6366f1" : "#e2e8f0")};
  border-radius: 12px; background: ${(p) => (p.$dragging ? "#eef2ff" : "white")};
  margin-bottom: 8px;
  transition: border-color 0.15s ease;
`;
const Cover = styled.div`
  width: 64px; height: 64px; border-radius: 8px; flex-shrink: 0;
  background: ${(p) => (p.$src ? `url(${p.$src}) center/cover no-repeat` : "#f1f5f9")};
`;
const EventBody = styled.div`flex: 1; min-width: 0;`;
const EventTitle = styled.div`font-weight: 600; color: #0f172a; font-size: 14px; margin-bottom: 4px;`;
const EventMeta = styled.div`color: #64748b; font-size: 12px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center;`;
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(76px, 1fr));
  gap: 6px;
  min-width: 540px;
`;
const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  min-height: 56px;
`;
const StatNum = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums;
`;
const StatNumMuted = styled.span`
  color: #94a3b8;
  font-weight: 500;
  font-size: 12px;
  margin-left: 3px;
`;
const StatCaption = styled.div`
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
  text-align: center;
  line-height: 1.2;
`;
const Handle = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px; color: #94a3b8; cursor: grab;
  &:hover { background: #f1f5f9; color: #475569; }
`;

// SaleTransaction.paymentBreakdown.totalAmount is stored in paise — divide by 100 for INR.
const fmtMoney = (n) =>
  `₹${(Number(n || 0) / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTH_ABBR[d.getMonth()];
  const year = d.getFullYear();
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  const h12 = d.getHours() % 12 || 12;
  const hh = String(h12).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} ${hh}:${mm} ${ampm}`;
};

export default function CategoryPageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(Api.CategoryPageDetail.replace(":id", id));
      setData(res.data?.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refreshStats = async () => {
    setRefreshing(true);
    try {
      await customClientAxios().post(Api.CategoryPageStatsRefresh.replace(":id", id));
      message.success("Stats refreshed");
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const onAddEvents = async (offeringIds) => {
    setPickerOpen(false);
    if (!offeringIds.length) return;
    try {
      const res = await customClientAxios().post(
        Api.CategoryPageEvents.replace(":id", id),
        { offeringIds }
      );
      setData(res.data?.data);
      message.success(`Added ${res.data?.added} event${res.data?.added === 1 ? "" : "s"}`);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to add events");
    }
  };

  const onRemove = async (slugId) => {
    try {
      const res = await customClientAxios().delete(
        Api.CategoryPageEventBySlug.replace(":id", id).replace(":slugId", slugId)
      );
      setData(res.data?.data);
      message.success("Removed");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to remove");
    }
  };

  // Reorder runs against the full events list. The "active" tab is the only place
  // DnD is enabled — past events are read-only — so we splice within active events
  // and merge them back into the full list, preserving past events' original order.
  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const all = Array.from(data.events || []);
    const activeIdxs = all.map((e, i) => (e.isPast ? -1 : i)).filter((i) => i >= 0);
    const [movedAt] = activeIdxs.splice(result.source.index, 1);
    activeIdxs.splice(result.destination.index, 0, movedAt);
    // Build new full list: write active events into their reordered slots, leave past in place.
    const reordered = all.slice();
    const activeOriginal = all.filter((e) => !e.isPast);
    const sourceMoved = activeOriginal[result.source.index];
    const newActiveOrder = activeOriginal.slice();
    newActiveOrder.splice(result.source.index, 1);
    newActiveOrder.splice(result.destination.index, 0, sourceMoved);
    let cursor = 0;
    for (let i = 0; i < reordered.length; i++) {
      if (!reordered[i].isPast) {
        reordered[i] = newActiveOrder[cursor++];
      }
    }
    setData((d) => ({ ...d, events: reordered }));
    try {
      await customClientAxios().patch(
        Api.CategoryPageEventsReorder.replace(":id", id),
        { slugIds: reordered.map((e) => e.slugId) }
      );
    } catch (e) {
      message.error("Failed to save order");
      load();
    }
  };

  if (loading) return <PageWrapper><Spin /></PageWrapper>;
  if (error) return <PageWrapper><Alert type="error" message={error} /></PageWrapper>;
  if (!data) return null;

  const stats = data.stats || {};
  const perEventMap = Object.fromEntries((stats.perEvent || []).map((p) => [p.slugId, p]));
  const existingOfferingIds = (data.events || []).map((e) => e.offeringId);

  return (
    <PageWrapper>
      <Header>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/category-pages")}>Back</Button>
          <TitleBlock>
            <Title>{data.name}</Title>
            <Subtitle>
              <code>/category/{data.slug}</code> &middot;{" "}
              <Tag color={data.status === "published" ? "green" : "default"} style={{ marginLeft: 4 }}>{data.status}</Tag>
            </Subtitle>
          </TitleBlock>
        </Space>
        <Space>
          {data.status === "published" && (
            <Tooltip title="Open public page">
              <Button
                icon={<EyeOutlined />}
                href={`https://growezy.club/category/${data.slug}`}
                target="_blank"
              >
                View live
              </Button>
            </Tooltip>
          )}
          <Button icon={<EditOutlined />} onClick={() => navigate(`/category-pages/${id}/edit`)}>Edit</Button>
        </Space>
      </Header>

      <Banner $src={data.bannerImage}>
        <BannerOverlay>
          <div>
            <BannerName>{data.name}</BannerName>
            {data.description && (
              <div style={{ opacity: 0.85, fontSize: 14, maxWidth: 720, marginTop: 6 }}>{data.description}</div>
            )}
          </div>
        </BannerOverlay>
      </Banner>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <SectionTitle style={{ margin: 0 }}>Overall stats</SectionTitle>
          <Space>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              {stats.lastRefreshedAt
                ? `Last refreshed ${new Date(stats.lastRefreshedAt).toLocaleString()}`
                : "Never refreshed"}
            </span>
            <Button icon={<ReloadOutlined />} loading={refreshing} onClick={refreshStats}>
              Refresh stats
            </Button>
          </Space>
        </div>
        <StatGrid>
          <StatBox>
            <StatValue>
              {stats.pageViews?.unique || 0}
              <StatValueMuted>/ {stats.pageViews?.total || 0}</StatValueMuted>
            </StatValue>
            <StatLabel>Category views</StatLabel>
            <StatHint>unique / total</StatHint>
          </StatBox>
          <StatBox>
            <StatValue>
              {stats.cardClicks?.unique || 0}
              <StatValueMuted>/ {stats.cardClicks?.total || 0}</StatValueMuted>
            </StatValue>
            <StatLabel>Card clicks</StatLabel>
            <StatHint>unique / total</StatHint>
          </StatBox>
          <StatBox>
            <StatValue>{stats.totalSales || 0}</StatValue>
            <StatLabel>Sales</StatLabel>
            <StatHint>
              across {(data.events || []).length} event{(data.events || []).length === 1 ? "" : "s"}
            </StatHint>
          </StatBox>
          <StatBox>
            <StatValue>{fmtMoney(stats.totalRevenue)}</StatValue>
            <StatLabel>Revenue</StatLabel>
            <StatHint>captured payments</StatHint>
          </StatBox>
          <StatBox>
            <StatValue>{fmtMoney(stats.totalPlatformFee)}</StatValue>
            <StatLabel>Platform Fee</StatLabel>
            <StatHint>sum across all events</StatHint>
          </StatBox>
          <StatBox>
            <StatValue>{(stats.overallConversionRate || 0).toFixed(2)}%</StatValue>
            <StatLabel>Conversion</StatLabel>
            <StatHint>visitor → sale</StatHint>
          </StatBox>
        </StatGrid>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <SectionTitle style={{ margin: 0 }}>Featured events</SectionTitle>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
            Add events
          </Button>
        </div>

        {(() => {
          const activeEvents = (data.events || []).filter((e) => !e.isPast);
          const pastEvents = (data.events || []).filter((e) => e.isPast);

          const renderRow = (e, idx, draggable) => {
            const s = perEventMap[e.slugId] || {};
            const inner = (prov, snap) => (
              <EventRow
                ref={prov?.innerRef}
                {...(prov?.draggableProps || {})}
                $dragging={snap?.isDragging}
                style={{
                  ...(prov?.draggableProps?.style || {}),
                  opacity: e.isPast ? 0.78 : 1,
                }}
              >
                {draggable ? (
                  <Handle {...prov.dragHandleProps}><DragOutlined /></Handle>
                ) : (
                  <Handle as="div" style={{ cursor: "default", visibility: "hidden" }}>
                    <DragOutlined />
                  </Handle>
                )}
                <Cover $src={e.coverImage} />
                <EventBody>
                  <EventTitle>{e.title || "(untitled)"}</EventTitle>
                  <EventMeta>
                    <span>{e.brandName || "—"}</span>
                    {e.isPast && <Tag color="default">Past</Tag>}
                    {e.eventEndAt && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        {e.isPast ? "Ended" : "Ends"} {fmtDateTime(e.eventEndAt)}
                      </span>
                    )}
                  </EventMeta>
                </EventBody>
                <StatsRow>
                  <Stat>
                    <StatNum>
                      {s.cardClicks?.unique || 0}
                      <StatNumMuted>/ {s.cardClicks?.total || 0}</StatNumMuted>
                    </StatNum>
                    <StatCaption>Clicks</StatCaption>
                  </Stat>
                  <Stat>
                    <StatNum>{s.offeringViews || 0}</StatNum>
                    <StatCaption>Offering views</StatCaption>
                  </Stat>
                  <Stat>
                    <StatNum>{s.sales || 0}</StatNum>
                    <StatCaption>Sales</StatCaption>
                  </Stat>
                  <Stat>
                    <StatNum>{fmtMoney(s.revenue)}</StatNum>
                    <StatCaption>Revenue</StatCaption>
                  </Stat>
                  <Stat>
                    <StatNum>{fmtMoney(s.platformFee)}</StatNum>
                    <StatCaption>Platform Fee</StatCaption>
                  </Stat>
                  <Stat>
                    <StatNum>{(s.conversionRate || 0).toFixed(2)}%</StatNum>
                    <StatCaption>Views → Sales</StatCaption>
                  </Stat>
                </StatsRow>
                <Popconfirm
                  title="Remove from category?"
                  onConfirm={() => onRemove(e.slugId)}
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </EventRow>
            );
            if (!draggable) return <div key={e.slugId}>{inner(null, null)}</div>;
            return (
              <Draggable key={e.slugId} draggableId={e.slugId} index={idx}>
                {(prov, snap) => inner(prov, snap)}
              </Draggable>
            );
          };

          return (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: "active",
                  label: `Active (${activeEvents.length})`,
                  children:
                    activeEvents.length === 0 ? (
                      <Empty description="No active events. Click 'Add events' to get started." />
                    ) : (
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="active-events">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {activeEvents.map((e, idx) => renderRow(e, idx, true))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ),
                },
                {
                  key: "past",
                  label: `Past (${pastEvents.length})`,
                  children:
                    pastEvents.length === 0 ? (
                      <Empty description="No past events yet." />
                    ) : (
                      <div>{pastEvents.map((e, idx) => renderRow(e, idx, false))}</div>
                    ),
                },
              ]}
            />
          );
        })()}
      </Card>

      <EventPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={onAddEvents}
        existingOfferingIds={existingOfferingIds}
      />
    </PageWrapper>
  );
}
